import ApiClient from '@afosto/api/ApiClient';

class Users {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Create a new user
   *
   * @param {Object} body - User object
   * @param {Object} options - The additional request options
   * @description Returns a new authorization response
   * @returns {Promise}
   */
  createUser(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/users`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Delete a user by id
   *
   * @param {string} id - Id that belongs to the user
   * @param {Object} options - The additional request options
   * @description Deletes a user
   * @returns {Promise}
   */
  deleteUser(id, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/${id}`,
      method: 'delete',
      ...options,
    });
  }

  /**
   * Get a single user by id
   *
   * @param {string} id - Id that belongs to the user
   * @param {Object} options - The additional request options
   * @description Returns a single user
   * @returns {Promise}
   */
  getUser(id, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Disable all tokens of given type a user
   *
   * @param {Object} body - Request reset object
   * @param {Object} options - The additional request options
   * @description Invalidate all pending tokens of given type for user
   * @returns {Promise}
   */
  invalidateTokens(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/tokens/invalidate`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Join a tenant based on invite token
   *
   * @param {Object} body - Reset object
   * @param {Object} options - The additional request options
   * @description Accept an invite, set the password and mark user&#39;s email as verified
   * @returns {Promise}
   */
  joinTenant(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/join`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Get a list of tokens
   *
   * @param {string} id - Id that belongs to the user
   * @param {string} type
   * @param {Object} options - The additional request options
   * @description Returns a list of tokens
   * @returns {Promise}
   */
  listTokens(type, id, options = {}) {
    const params = {
      type,
    };

    return this.apiClient.request({
      url: `/iam/users/${id}/tokens`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get a list of users
   *
   * @param {Object} query - The optional path parameters
   * @param {Number} [query.isDeleted]
   * @param {Number} [query.isPendingInvite]
   * @param {Object} options - The additional request options
   * @description Returns a list of users
   * @returns {Promise}
   */
  listUsers(query = {}, options = {}) {
    const params = {
      is_deleted: query.isDeleted,
      is_pending_invite: query.isPendingInvite,
    };

    return this.apiClient.request({
      url: `/iam/users`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Request a token for the user
   *
   * @param {Object} body - Request reset object
   * @param {Object} options - The additional request options
   * @description So the user can receive a password reset mail or confirm mail
   * @returns {Promise}
   */
  requestToken(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/tokens`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Reset a user&#39;s credentials
   *
   * @param {Object} body - Reset object
   * @param {Object} options - The additional request options
   * @description So the user can login
   * @returns {Promise}
   */
  resetPassword(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/password`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Update a user by id
   *
   * @param {string} id - Id that belongs to the user
   * @param {Object} body - User object
   * @param {Object} options - The additional request options
   * @description Updates a user
   * @returns {Promise}
   */
  updateUser(id, body, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/${id}`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Update a user&#39;s roles by id
   *
   * @param {string} id - Id that belongs to the user
   * @param {Object} body - Array of role id&#39;s
   * @param {Object} options - The additional request options
   * @description Updates the roles that belong to a user
   * @returns {Promise}
   */
  updateUserRoles(id, body, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/${id}/roles`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Mark user&#39;s email as verified
   *
   * @param {Object} body - Verify object
   * @param {Object} options - The additional request options
   * @description So the user can login
   * @returns {Promise}
   */
  verifyUser(body, options = {}) {
    return this.apiClient.request({
      url: `/iam/users/verify`,
      method: 'post',
      data: body,
      ...options,
    });
  }
}

export const users = new Users();

export default Users;
